import React, { useState, useEffect, useMemo } from 'react';

interface UpdateSource {
  url: string;
  label: string;
  type: 'web' | 'x';
}

interface LiveUpdate {
  id: string;
  timestamp: string;
  text: string;
  detail: string | null;
  source: UpdateSource;
  isMajor: boolean;
  createdAt: string;
}

interface LiveUpdatesConfig {
  status: 'developing' | 'rapid' | 'settled' | 'inactive';
  lastCheckedAt: string | null;
  updates: LiveUpdate[];
}

interface Props {
  liveUpdates: LiveUpdatesConfig;
  slug: string;
}

const STATUS_CONFIG = {
  developing: {
    label: 'DEVELOPING',
    color: '#059669',
    bg: '#ecfdf5',
    border: '#a7f3d0',
    pulse: true,
  },
  rapid: {
    label: 'FAST-MOVING',
    color: '#d97706',
    bg: '#fffbeb',
    border: '#fde68a',
    pulse: true,
  },
  settled: {
    label: 'LATEST',
    color: '#64748b',
    bg: '#f8fafc',
    border: '#e2e8f0',
    pulse: false,
  },
  inactive: {
    label: 'INACTIVE',
    color: '#94a3b8',
    bg: '#f8fafc',
    border: '#e2e8f0',
    pulse: false,
  },
} as const;

const INITIAL_VISIBLE = 4;

function getRelativeDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    if (todayStart.getTime() === dateStart.getTime()) return 'Today';
  }

  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  const dateLabel = getRelativeDate(dateStr);
  if (dateLabel === 'Today') {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }
  return dateLabel;
}

function relativeTimeAgo(dateStr: string): string {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function getLastViewedKey(slug: string): string {
  return `twygs:liveUpdates:${slug}:lastViewed`;
}

const XLogo = ({ size = 10, color = '#64748b' }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const LinkIcon = ({ size = 10, color = '#94a3b8' }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

function UpdateRow({
  update,
  isLast,
  isNew,
}: {
  update: LiveUpdate;
  isLast: boolean;
  isNew: boolean;
}) {
  const [showDetail, setShowDetail] = useState(false);
  const hasDetail = !!update.detail;
  const timeLabel = formatTime(update.timestamp);

  return (
    <div style={{ display: 'flex', gap: '14px', padding: '0 0 0 2px' }}>
      {/* Timeline column */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '14px', flexShrink: 0, paddingTop: '6px' }}>
        <div style={{
          width: update.isMajor ? '10px' : '7px',
          height: update.isMajor ? '10px' : '7px',
          borderRadius: '50%',
          background: isNew ? '#059669' : update.isMajor ? '#475569' : '#d4d4d8',
          border: isNew ? '2px solid #05966930' : 'none',
          boxShadow: isNew ? '0 0 0 3px #05966912' : 'none',
          flexShrink: 0,
        }} />
        {!isLast && (
          <div style={{ width: '1.5px', flex: 1, background: '#e7e5e4', marginTop: '4px' }} />
        )}
      </div>

      {/* Content */}
      <div style={{ flex: 1, paddingBottom: isLast ? '0' : '14px', minWidth: 0 }}>
        {/* Timestamp + source row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
          <span style={{
            fontSize: '11px', fontWeight: 700,
            color: isNew ? '#059669' : '#a1a1aa',
            fontFamily: "'DM Sans', sans-serif",
          }}>
            {timeLabel}
          </span>
          {isNew && (
            <span style={{
              fontSize: '9px', fontWeight: 800, color: '#059669',
              fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.08em',
              padding: '1px 5px', borderRadius: '4px', background: '#ecfdf5',
            }}>
              NEW
            </span>
          )}
          <a
            href={update.source.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '3px',
              fontSize: '10px', fontWeight: 600, color: '#a1a1aa',
              textDecoration: 'none', fontFamily: "'DM Sans', sans-serif", marginLeft: 'auto',
            }}
          >
            {update.source.type === 'x'
              ? <XLogo size={9} color="#a1a1aa" />
              : <LinkIcon size={9} color="#a1a1aa" />}
            {update.source.label}
          </a>
        </div>

        {/* Update text */}
        <p
          onClick={hasDetail ? () => setShowDetail(!showDetail) : undefined}
          style={{
            fontSize: '13.5px',
            fontWeight: update.isMajor ? 700 : 500,
            color: update.isMajor ? '#0f172a' : '#475569',
            margin: 0, lineHeight: '1.45',
            fontFamily: "'Newsreader', Georgia, serif",
            cursor: hasDetail ? 'pointer' : 'default',
          }}
        >
          {update.text}
          {hasDetail && !showDetail && (
            <span style={{
              fontSize: '11px', fontWeight: 700, color: '#94a3b8',
              fontFamily: "'DM Sans', sans-serif", marginLeft: '6px', cursor: 'pointer',
            }}>
              ﹢ detail
            </span>
          )}
        </p>

        {/* Expanded detail */}
        {showDetail && update.detail && (
          <p style={{
            fontSize: '12.5px', color: '#64748b', margin: '5px 0 0',
            lineHeight: '1.5', fontFamily: "'Newsreader', Georgia, serif",
            paddingLeft: '10px', borderLeft: '2px solid #e7e5e4',
          }}>
            {update.detail}
          </p>
        )}
      </div>
    </div>
  );
}

export default function LiveUpdatesBox({ liveUpdates, slug }: Props) {
  const [expanded, setExpanded] = useState(true);
  const [showAll, setShowAll] = useState(false);

  const status = STATUS_CONFIG[liveUpdates.status] ?? STATUS_CONFIG.developing;
  const updates = liveUpdates.updates;

  // New update detection via localStorage
  const newUpdateIds = useMemo(() => {
    try {
      const key = getLastViewedKey(slug);
      const lastViewed = localStorage.getItem(key);
      if (!lastViewed) {
        return new Set(updates.slice(0, 3).map((u) => u.id));
      }
      const lastViewedDate = new Date(lastViewed);
      return new Set(
        updates.filter((u) => new Date(u.createdAt) > lastViewedDate).map((u) => u.id),
      );
    } catch {
      return new Set<string>();
    }
  }, [updates, slug]);

  // Mark as viewed when expanded
  useEffect(() => {
    if (expanded && updates.length > 0) {
      try {
        localStorage.setItem(getLastViewedKey(slug), new Date().toISOString());
      } catch {}
    }
  }, [expanded, slug, updates.length]);

  const newCount = newUpdateIds.size;
  const visibleUpdates = showAll ? updates : updates.slice(0, INITIAL_VISIBLE);
  const hiddenCount = updates.length - INITIAL_VISIBLE;

  const checkedLabel = liveUpdates.lastCheckedAt
    ? `Checked ${relativeTimeAgo(liveUpdates.lastCheckedAt)}`
    : null;

  return (
    <div className="max-w-[812px] mx-auto px-4 md:px-0 mt-6 mb-2" style={{ fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      <style>{`
        @keyframes pulse-live {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>

      <div style={{
        borderRadius: '14px',
        border: `1px solid ${status.border}`,
        background: 'white',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <button
          onClick={() => setExpanded(!expanded)}
          style={{
            width: '100%', padding: '12px 18px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: status.bg, border: 'none',
            borderBottom: expanded ? `1px solid ${status.border}` : 'none',
            cursor: 'pointer',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '8px', height: '8px', borderRadius: '50%',
              background: status.color,
              animation: status.pulse ? 'pulse-live 2s ease-in-out infinite' : 'none',
              boxShadow: `0 0 0 3px ${status.color}15`,
            }} />
            <span style={{
              fontSize: '11px', fontWeight: 800, color: status.color,
              fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.08em',
            }}>
              {status.label}
            </span>
            <span style={{
              fontSize: '12px', fontWeight: 600, color: '#475569',
              fontFamily: "'DM Sans', sans-serif",
            }}>
              This story is being updated
            </span>
            {newCount > 0 && (
              <span style={{
                fontSize: '10px', fontWeight: 800, color: 'white',
                background: status.color, padding: '2px 7px', borderRadius: '5px',
                fontFamily: "'DM Sans', sans-serif",
              }}>
                {newCount} new
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {checkedLabel && (
              <span style={{
                fontSize: '10px', fontWeight: 600, color: '#a1a1aa',
                fontFamily: "'DM Sans', sans-serif",
              }}>
                {checkedLabel}
              </span>
            )}
            <svg
              width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
        </button>

        {/* Timeline */}
        {expanded && (
          <div style={{ padding: '16px 18px 10px' }}>
            {(() => {
              let currentDateLabel: string | null = null;
              const elements: React.ReactNode[] = [];

              visibleUpdates.forEach((update, i) => {
                const dateLabel = getRelativeDate(update.timestamp);

                if (dateLabel !== currentDateLabel) {
                  currentDateLabel = dateLabel;
                  elements.push(
                    <div key={`date-${dateLabel}-${i}`} style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: i === 0 ? '0 0 10px 0' : '4px 0 10px 0',
                    }}>
                      <span style={{
                        fontSize: '10px', fontWeight: 800, color: '#a1a1aa',
                        fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.08em',
                        textTransform: 'uppercase', whiteSpace: 'nowrap',
                      }}>
                        {dateLabel}
                      </span>
                      <div style={{ flex: 1, height: '1px', background: '#f1f0ee' }} />
                    </div>,
                  );
                }

                elements.push(
                  <UpdateRow
                    key={update.id}
                    update={update}
                    isNew={newUpdateIds.has(update.id)}
                    isLast={i === visibleUpdates.length - 1 && (showAll || hiddenCount <= 0)}
                  />,
                );
              });

              return elements;
            })()}

            {/* Show older */}
            {!showAll && hiddenCount > 0 && (
              <button
                onClick={() => setShowAll(true)}
                style={{
                  width: '100%', padding: '8px', borderRadius: '8px',
                  border: 'none', background: '#f8fafc', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: '4px', marginTop: '6px',
                }}
              >
                <span style={{
                  fontSize: '11px', fontWeight: 700, color: '#94a3b8',
                  fontFamily: "'DM Sans', sans-serif",
                }}>
                  {hiddenCount} earlier updates
                </span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
